import {Component, ElementRef, Injector, OnDestroy, OnInit, Renderer2, ChangeDetectionStrategy} from '@angular/core';
import {Router} from '@angular/router';
import {MenuService} from '../../core/menu/menu.service';
import {SettingsService} from '../../core/settings/settings.service';

/**
 * Sidebar sin jQuery: accordion de submenús, fly-out en modo colapsado y cierre
 * del offcanvas por click externo, todo con DOM nativo + Renderer2.
 */
@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false
})
export class SidebarComponent implements OnInit, OnDestroy {

  menuItems: Array<any>;
  router: Router;
  private unlistenDocClick: (() => void) | null = null;
  private unlistenFloatingDocClick: (() => void) | null = null;

  constructor(public menu: MenuService,
              public settings: SettingsService,
              public injector: Injector,
              private el: ElementRef,
              private renderer: Renderer2) {
    this.menuItems = menu.getMenu();
  }

  ngOnInit() {
    this.router = this.injector.get(Router);
    this.router.events.subscribe(() => {
      // cerrar submenús flotantes al cambiar de ruta
      this.removeFloatingNav();
      window.scrollTo(0, 0);
      // cerrar el offcanvas al navegar
      this.settings.layout.asideToggled = false;
    });
    // cerrar el sidebar (offcanvas) al clickear afuera
    this.anyClickClose();
  }

  anyClickClose() {
    this.unlistenDocClick = this.renderer.listen('document', 'click', (e: Event) => {
      const target = e.target as HTMLElement;
      if (target && !target.closest('.aside')) {
        this.settings.layout.asideToggled = false;
      }
    });
  }

  ngOnDestroy() {
    if (this.unlistenDocClick) { this.unlistenDocClick(); }
    if (this.unlistenFloatingDocClick) { this.unlistenFloatingDocClick(); }
  }

  toggleSubmenuClick(event: Event) {
    if (!this.isSidebarCollapsed() && !this.isSidebarCollapsedText() && !this.isEnabledHover()) {
      event.preventDefault();
      const targetEl = (event.target as HTMLElement);
      const anchor: HTMLElement | null = targetEl.tagName === 'A' ? targetEl : targetEl.closest('a');
      const ul = anchor ? (anchor.nextElementSibling as HTMLElement) : null;
      const parentNav = ul ? ul.closest('.sidebar-subnav') : null;

      // cerrar los otros submenús (que no sean ancestro ni el propio)
      document.querySelectorAll<HTMLElement>('.sidebar-subnav').forEach((elem) => {
        if (elem !== parentNav && elem !== ul) {
          this.closeMenu(elem);
        }
      });

      if (!ul) { return; }

      // cualquier submenú hijo arranca cerrado
      ul.querySelectorAll<HTMLElement>('.sidebar-subnav').forEach((elem) => this.closeMenu(elem));

      // toggle de altura
      if (parseInt(String(ul.offsetHeight), 10)) {
        this.closeMenu(ul);
      } else {
        const onEnd = () => {
          this.renderer.setStyle(ul, 'height', 'auto');
          ul.removeEventListener('transitionend', onEnd);
        };
        ul.addEventListener('transitionend', onEnd);
        this.renderer.setStyle(ul, 'height', `${ul.scrollHeight}px`);
        this.renderer.addClass(ul, 'opening');
      }
    }
  }

  // Cierra el submenú colapsando la altura
  closeMenu(elem: HTMLElement) {
    this.renderer.setStyle(elem, 'height', `${elem.scrollHeight}px`); // fija altura
    // forzar reflow para que la transición a 0 tome efecto
    void elem.offsetHeight;
    this.renderer.setStyle(elem, 'height', '0');
    this.renderer.removeClass(elem, 'opening');
  }

  toggleSubmenuHover(event: Event) {
    const self = this;
    if (this.isSidebarCollapsed() || this.isSidebarCollapsedText() || this.isEnabledHover()) {
      event.preventDefault();
      this.removeFloatingNav();

      const targetEl = (event.target as HTMLElement);
      const anchor: HTMLElement | null = targetEl.tagName === 'A' ? targetEl : targetEl.closest('a');
      const ul = anchor ? (anchor.nextElementSibling as HTMLElement) : null;
      if (!ul) { return; }

      const aside = this.el.nativeElement.querySelector('.aside') as HTMLElement
        || document.querySelector('.aside') as HTMLElement;
      const asideInner = aside ? aside.querySelector('.aside-inner') as HTMLElement : null;
      const sidebar = asideInner ? asideInner.querySelector('.sidebar') as HTMLElement : null;
      if (!aside || !asideInner || !sidebar || !anchor?.parentElement) { return; }

      const mar = parseInt(getComputedStyle(asideInner).paddingTop, 10)
        + parseInt(getComputedStyle(aside).paddingTop, 10);
      const itemTop = (anchor.parentElement.offsetTop + mar) - sidebar.scrollTop;

      const floatingNav = ul.cloneNode(true) as HTMLElement;
      this.renderer.appendChild(aside, floatingNav);
      const vwHeight = window.innerHeight;

      this.renderer.removeClass(floatingNav, 'opening');
      this.renderer.addClass(floatingNav, 'nav-floating');
      this.renderer.setStyle(floatingNav, 'position', this.settings.layout.isFixed ? 'fixed' : 'absolute');
      this.renderer.setStyle(floatingNav, 'top', `${itemTop}px`);
      this.renderer.setStyle(floatingNav, 'height', 'auto');
      if (floatingNav.offsetHeight + itemTop > vwHeight) {
        this.renderer.setStyle(floatingNav, 'bottom', '0');
      }

      floatingNav.addEventListener('mouseleave', () => floatingNav.remove());
      floatingNav.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          const routeTo = (link as HTMLElement).getAttribute('route');
          if (routeTo) { self.router.navigate([routeTo]); }
        });
      });

      this.listenForExternalClicks();
    }
  }

  listenForExternalClicks() {
    if (this.unlistenFloatingDocClick) { this.unlistenFloatingDocClick(); }
    this.unlistenFloatingDocClick = this.renderer.listen('document', 'click', (e: Event) => {
      const target = e.target as HTMLElement;
      if (target && !target.closest('.aside')) {
        this.removeFloatingNav();
        if (this.unlistenFloatingDocClick) {
          this.unlistenFloatingDocClick();
          this.unlistenFloatingDocClick = null;
        }
      }
    });
  }

  removeFloatingNav() {
    document.querySelectorAll('.nav-floating').forEach((el) => el.remove());
  }

  isSidebarCollapsed() {
    return this.settings.layout.isCollapsed;
  }

  isSidebarCollapsedText() {
    return this.settings.layout.isCollapsedText;
  }

  isEnabledHover() {
    return this.settings.layout.asideHover;
  }
}
